import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import {
  Tabs,
  Table,
  Badge,
  Avatar,
  DropdownMenu,
  Modal,
  Button,
  Input,
  Text,
  Card,
} from '@teals/ui-web';
import type { TabItem } from '@teals/ui-web';
import type { Column } from '@teals/ui-web';

type BusinessTab = 'members' | 'roles' | 'settings';

interface Member {
  id: string;
  name: string;
  email: string;
  role: 'Owner' | 'Admin' | 'Builder' | 'Viewer';
  status: 'Active' | 'Inactive';
  avatarUrl?: string;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  sentAt: string;
}

const MOCK_MEMBERS: Member[] = [
  { id: '1', name: 'Sarah Chen', email: 'sarah@luminafactory.io', role: 'Owner', status: 'Active' },
  { id: '2', name: 'Marcus Rivera', email: 'marcus@luminafactory.io', role: 'Admin', status: 'Active' },
  { id: '3', name: 'Aisha Patel', email: 'aisha@luminafactory.io', role: 'Builder', status: 'Active' },
  { id: '4', name: 'James O\'Brien', email: 'james@luminafactory.io', role: 'Builder', status: 'Active' },
  { id: '5', name: 'Yuki Tanaka', email: 'yuki@luminafactory.io', role: 'Viewer', status: 'Inactive' },
];

const MOCK_INVITATIONS: Invitation[] = [
  { id: 'inv-1', email: 'new.member@company.com', role: 'Builder', sentAt: '2 hours ago' },
  { id: 'inv-2', email: 'contractor@external.io', role: 'Viewer', sentAt: '1 day ago' },
];

const ROLES = [
  {
    name: 'Owner',
    variant: 'primary' as const,
    permissions: [
      'Full account access and billing management',
      'Transfer ownership to another member',
      'Delete the organisation',
    ],
  },
  {
    name: 'Admin',
    variant: 'info' as const,
    permissions: [
      'Manage members, roles, and organisation settings',
      'Create, edit, and publish agents',
      'View analytics and manage partners',
    ],
  },
  {
    name: 'Builder',
    variant: 'success' as const,
    permissions: [
      'Create and edit agents in draft and sandbox',
      'Access the playground and testing tools',
      'View agent analytics for own agents',
    ],
  },
  {
    name: 'Viewer',
    variant: 'neutral' as const,
    permissions: [
      'View agents and their configurations (read-only)',
      'View organisation-level analytics',
      'Cannot modify agents or settings',
    ],
  },
];

const TABS: TabItem[] = [
  { id: 'members', label: 'Members', badge: MOCK_MEMBERS.length },
  { id: 'roles', label: 'Roles' },
  { id: 'settings', label: 'Settings' },
];

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 1100px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Subtitle = styled.span`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const TabContent = styled.div`
  margin-top: 1rem;
`;

const Section = styled.div`
  margin-top: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 0.75rem;
`;

const MemberCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ModalForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const SelectLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  min-height: 44px;
  border: 2px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  color: var(--text-primary);
  background: var(--bg-surface);
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: var(--accent);
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const InvitationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const InvitationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const InvitationMeta = styled.span`
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const RolesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
`;

const RoleCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RoleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PermissionList = styled.ul`
  margin: 0;
  padding: 0 0 0 1.25rem;
  list-style: disc;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const PermissionItem = styled.li`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const SettingsForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  max-width: 480px;
`;

const ROLE_BADGE_VARIANT: Record<string, 'primary' | 'info' | 'success' | 'neutral'> = {
  Owner: 'primary',
  Admin: 'info',
  Builder: 'success',
  Viewer: 'neutral',
};

const STATUS_BADGE_VARIANT: Record<string, 'success' | 'neutral'> = {
  Active: 'success',
  Inactive: 'neutral',
};

const BusinessCenter: React.FC = () => {
  const [activeTab, setActiveTab] = useState<BusinessTab>('members');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Builder');
  const [orgName, setOrgName] = useState('Acme Corporation');
  const [orgDescription, setOrgDescription] = useState(
    'AI-powered solutions for enterprise workflow automation.'
  );

  const handleInvite = () => {
    setInviteOpen(false);
    setInviteEmail('');
    setInviteRole('Builder');
  };

  const memberColumns: Column<Member>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        width: '30%',
        render: (_val: string, row: Member) => (
          <MemberCell>
            <Avatar name={row.name} src={row.avatarUrl} size="sm" />
            <Text variant="body2" style={{ fontWeight: 500 }}>
              {row.name}
            </Text>
          </MemberCell>
        ),
      },
      { key: 'email', header: 'Email' },
      {
        key: 'role',
        header: 'Role',
        render: (val: string) => (
          <Badge variant={ROLE_BADGE_VARIANT[val] ?? 'neutral'} size="sm">
            {val}
          </Badge>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (val: string) => (
          <Badge variant={STATUS_BADGE_VARIANT[val] ?? 'neutral'} size="sm">
            {val}
          </Badge>
        ),
      },
      {
        key: 'actions',
        header: '',
        width: '48px',
        align: 'right' as const,
        render: (_val: unknown, row: Member) => (
          <DropdownMenu
            align="right"
            items={[
              { label: 'Edit Role', onClick: () => {} },
              { divider: true, label: '' },
              {
                label: 'Remove',
                variant: 'danger',
                disabled: row.role === 'Owner',
                onClick: () => {},
              },
            ]}
          />
        ),
      },
    ],
    []
  );

  return (
    <Page>
      <Header>
        <HeaderText>
          <Text variant="h3">Business Center</Text>
          <Subtitle>{orgName}</Subtitle>
        </HeaderText>
        {activeTab === 'members' && (
          <Button variant="primary" onClick={() => setInviteOpen(true)}>
            Invite Member
          </Button>
        )}
      </Header>

      <Tabs
        items={TABS}
        activeTab={activeTab}
        onChange={(id) => setActiveTab(id as BusinessTab)}
        variant="underline"
      />

      <TabContent>
        {activeTab === 'members' && (
          <>
            <Table columns={memberColumns} data={MOCK_MEMBERS} rowKey="id" />

            <Section>
              <SectionTitle>Pending Invitations</SectionTitle>
              <Card padding={0} elevation="sm">
                {MOCK_INVITATIONS.length === 0 ? (
                  <InvitationRow>
                    <Text variant="body2" color="var(--text-secondary)">
                      No pending invitations.
                    </Text>
                  </InvitationRow>
                ) : (
                  MOCK_INVITATIONS.map((inv) => (
                    <InvitationRow key={inv.id}>
                      <InvitationInfo>
                        <Avatar name={inv.email} size="sm" />
                        <div>
                          <Text variant="body2" style={{ fontWeight: 500 }}>
                            {inv.email}
                          </Text>
                          <InvitationMeta>
                            Invited as {inv.role} &middot; {inv.sentAt}
                          </InvitationMeta>
                        </div>
                      </InvitationInfo>
                      <Button variant="ghost" size="sm">
                        Revoke
                      </Button>
                    </InvitationRow>
                  ))
                )}
              </Card>
            </Section>
          </>
        )}

        {activeTab === 'roles' && (
          <RolesGrid>
            {ROLES.map((role) => (
              <RoleCard key={role.name} padding={20} elevation="sm">
                <RoleHeader>
                  <Badge variant={role.variant} size="md">
                    {role.name}
                  </Badge>
                </RoleHeader>
                <PermissionList>
                  {role.permissions.map((perm, i) => (
                    <PermissionItem key={i}>{perm}</PermissionItem>
                  ))}
                </PermissionList>
              </RoleCard>
            ))}
          </RolesGrid>
        )}

        {activeTab === 'settings' && (
          <SettingsForm>
            <Input
              label="Organisation Name"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              fullWidth
            />
            <Input
              label="Description"
              value={orgDescription}
              onChange={(e) => setOrgDescription(e.target.value)}
              fullWidth
            />
            <div>
              <Button variant="primary">Save Changes</Button>
            </div>
          </SettingsForm>
        )}
      </TabContent>

      <Modal isOpen={inviteOpen} onClose={() => setInviteOpen(false)} title="Invite Member">
        <ModalForm>
          <Input
            label="Email Address"
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="colleague@company.com"
            fullWidth
          />
          <SelectWrapper>
            <SelectLabel>Role</SelectLabel>
            <Select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
              <option value="Admin">Admin</option>
              <option value="Builder">Builder</option>
              <option value="Viewer">Viewer</option>
            </Select>
          </SelectWrapper>
          <ModalActions>
            <Button variant="ghost" onClick={() => setInviteOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleInvite} disabled={!inviteEmail.trim()}>
              Send Invitation
            </Button>
          </ModalActions>
        </ModalForm>
      </Modal>
    </Page>
  );
};

export default BusinessCenter;
